import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw } from 'lucide-react';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../components/ui/input-otp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Verify() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!email) {
      navigate('/register');
    }
  }, [email, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('Por favor ingresa el código completo');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/verify`, { email, code });
      toast.success('¡Cuenta verificada! Ahora puedes iniciar sesión.');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al verificar el código';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setResendLoading(true);

    try {
      await axios.post(`${API}/auth/resend-code`, { email });
      toast.success('Código reenviado. Revisa tu correo.');
      setCanResend(false);
      setCountdown(60);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al reenviar el código';
      toast.error(message);
    } finally {
      setResendLoading(false);
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F8FAFC] p-4">
      <div className="w-full max-w-md bg-background border shadow-[0_12px_32px_rgba(0,0,0,0.08)] p-8 rounded-sm relative overflow-hidden">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center">
            <ShieldCheck className="h-8 w-8 text-accent" />
          </div>
        </div>

        {/* Header */}
        <div className="text-center space-y-2 mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-primary">
            Verifica tu email
          </h1>
          <p className="text-base text-muted-foreground">
            Enviamos un código de 6 dígitos a
          </p>
          <p className="text-sm font-medium text-primary" data-testid="verify-email-display">
            {email}
          </p>
        </div>

        {/* OTP Input */}
        <div className="flex justify-center mb-8">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={(value) => setCode(value)}
            data-testid="verify-otp-input"
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} className="h-12 w-12 text-lg rounded-sm" />
              <InputOTPSlot index={1} className="h-12 w-12 text-lg rounded-sm" />
              <InputOTPSlot index={2} className="h-12 w-12 text-lg rounded-sm" />
              <InputOTPSlot index={3} className="h-12 w-12 text-lg rounded-sm" />
              <InputOTPSlot index={4} className="h-12 w-12 text-lg rounded-sm" />
              <InputOTPSlot index={5} className="h-12 w-12 text-lg rounded-sm" />
            </InputOTPGroup>
          </InputOTP>
        </div>

        {/* Verify Button */}
        <Button
          onClick={handleVerify}
          disabled={loading || code.length !== 6}
          className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium transition-all active:scale-95 mb-4"
          data-testid="verify-submit-button"
        >
          {loading ? 'Verificando...' : 'Verificar cuenta'}
        </Button>

        {/* Resend Button */}
        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            ¿No recibiste el código?
          </p>
          <Button
            variant="ghost"
            onClick={handleResend}
            disabled={!canResend || resendLoading}
            className="h-11 px-4 rounded-sm font-medium transition-all hover:bg-accent/10 hover:text-accent"
            data-testid="verify-resend-button"
          >
            {resendLoading ? (
              'Reenviando...'
            ) : canResend ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Reenviar código
              </span>
            ) : (
              `Reenviar en ${countdown}s`
            )}
          </Button>
        </div>

        {/* Info */}
        <div className="mt-6 p-4 bg-secondary/50 rounded-sm border border-border">
          <p className="text-xs text-muted-foreground text-center">
            El código expira en 15 minutos
          </p>
        </div>
      </div>
    </div>
  );
}